/**
 * Responsibility: Implement persist behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import localforage from 'localforage'
import { 
  WorldColorScheme, 
  type GameState, 
  type AtlasNode, 
  type Attribute, 
  SupabasePersistenceAdapter,
  LocalPersistenceAdapter,
  SyncManager,
  MediaAdapter,
  getAppImportMetaEnv,
  gzip,
  gunzip,
  withTimeout as withFirestoreTimeout,
  type ImportResult,
  type SharedPlayerProfile,
  type SharedStarterKit,
  type SaveMeta
} from "@anyquest/shared-core"
import { locationKey, generateNpcId, normalizeSkills, normalizeAttributeMetadata, normalizeProgressClocks, normalizeScheduledEffectJobs } from './state'
import { parseGameEventState } from './entities/game-event-artifact'

// ============================================================
// PERSIST.TS — Hybrid Storage Engine
// Supabase (via SupabasePersistenceAdapter): gzip'd + base64-encoded game state (minus history)
// IndexedDB (localforage + LocalPersistenceAdapter): history array + image blobs (device-local)
// ============================================================

const cloudAdapter = new SupabasePersistenceAdapter()
const localAdapter = new LocalPersistenceAdapter()
const syncManager = new SyncManager(cloudAdapter)
const mediaAdapter = new MediaAdapter()

localforage.config({
  name: 'AnyQuest',
  storeName: 'vision_cache',
})

const PENDING_INDEX_PREFIX = 'pending_saves_index_'
const SAVE_BACKUP_PREFIX = 'save_conflict_backup_'
const LOCAL_SAVE_INDEX_PREFIX = 'local_saves_index_'
const LOCAL_SAVE_PREFIX = 'local_save_'
const LOCAL_PUBLIC_BLUEPRINTS_KEY = 'am2_public_blueprints_v1'
const LOCAL_PUBLIC_SCENARIOS_KEY = 'am2_public_scenarios_v1'
const LOCAL_PUBLIC_VESSELS_KEY = 'am2_public_vessels_v1'
const LOCAL_PUBLIC_STARTERS_KEY = 'am2_public_starters_v1'
const PERSIST_MODE_KEY = 'am2_persist_mode'
const SAVE_BACKUP_LIMIT = 30
const appEnv = getAppImportMetaEnv()
const E2E_MOCK_MODE = appEnv.VITE_E2E_MOCK_MODE === '1'
const DEFAULT_LOCAL_ONLY_PERSIST = appEnv.VITE_LOCAL_ONLY_PERSIST === '1' || E2E_MOCK_MODE

export type PersistenceMode = 'hybrid' | 'local-only'
export type { ImportResult, SharedPlayerProfile, SharedStarterKit, SaveMeta }

interface LocalSaveEntry {
  uid: string
  saveId: string
  lastStateTimestamp: number
  compressed: true
  stateString: string
}

function normalizePersistenceMode(value: unknown): PersistenceMode {
  return value === 'local-only' ? 'local-only' : 'hybrid'
}

export function getPersistenceMode(): PersistenceMode {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_LOCAL_ONLY_PERSIST ? 'local-only' : 'hybrid'
  }
  const raw = localStorage.getItem(PERSIST_MODE_KEY)
  if (!raw) return DEFAULT_LOCAL_ONLY_PERSIST ? 'local-only' : 'hybrid'
  return normalizePersistenceMode(raw)
}

export function setPersistenceMode(mode: PersistenceMode): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(PERSIST_MODE_KEY, normalizePersistenceMode(mode))
}

function isLocalOnlyPersistence(): boolean {
  return getPersistenceMode() === 'local-only'
}

// --- Path helpers ---

function pendingIndexKey(uid: string): string {
  return `${PENDING_INDEX_PREFIX}${uid}`
}

function pendingSaveKey(uid: string, saveId: string): string {
  return `pending_save_${uid}_${saveId}`
}

function readLocalPublicArtifactMap(storageKey: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {}
  } catch {
    return {}
  }
}

function saveLocalPublicArtifact(storageKey: string, code: string, payload: string): void {
  try {
    const current = readLocalPublicArtifactMap(storageKey)
    current[String(code || '').toUpperCase()] = String(payload)
    localStorage.setItem(storageKey, JSON.stringify(current))
  } catch {
    // Non-fatal local persistence failure.
  }
}

function readLocalPublicArtifact(storageKey: string, code: string): string | null {
  try {
    const current = readLocalPublicArtifactMap(storageKey)
    return current[String(code || '').toUpperCase()] ?? null
  } catch {
    return null
  }
}

function localSaveIndexKey(uid: string): string {
  return `${LOCAL_SAVE_INDEX_PREFIX}${uid}`
}

function localSaveKey(uid: string, saveId: string): string {
  return `${LOCAL_SAVE_PREFIX}${uid}_${saveId}`
}

function stripImages(state: GameState): Omit<GameState, 'visionUrl' | 'portraitUrl'> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { visionUrl: _v, portraitUrl: _p, ...textState } = state
  const companions = (textState.companions || []).map(c => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { portraitUrl: _cp, ...cRest } = c
    return cRest as typeof c
  })
  return { ...textState, companions }
}

async function cacheLocalArtifacts(uid: string, saveId: string, state: GameState): Promise<void> {
  const writes: Array<Promise<unknown>> = []
  const isCloud = !isLocalOnlyPersistence() && uid && !uid.startsWith('e2e_')

  if (state.portraitUrl?.startsWith('data:')) {
    writes.push(localforage.setItem(`portrait_${saveId}`, state.portraitUrl))
    if (isCloud) {
      writes.push(mediaAdapter.uploadImage(uid, saveId, 'portrait', state.portraitUrl)
        .then(url => { state.portraitUrl = url }))
    }
  }
  if (state.companions) {
    for (const companion of state.companions) {
      if (companion.portraitUrl?.startsWith('data:')) {
        writes.push(localforage.setItem(`companion_portrait_${saveId}_${companion.name}`, companion.portraitUrl))
        if (isCloud) {
          writes.push(mediaAdapter.uploadImage(uid, saveId, `companion_${companion.name}`, companion.portraitUrl)
            .then(url => { companion.portraitUrl = url }))
        }
      }
    }
  }
  for (const [key, node] of Object.entries(state.atlas.nodes)) {
    if (node.visionUrl?.startsWith('data:')) {
      writes.push(localforage.setItem(`vision_${saveId}_${key}`, node.visionUrl))
      if (isCloud) {
        writes.push(mediaAdapter.uploadImage(uid, saveId, `vision_${key}`, node.visionUrl)
          .then(url => { node.visionUrl = url }))
      }
    }
  }
  writes.push(localforage.setItem(`history_${saveId}`, state.history))
  await Promise.all(writes)
}

async function loadLocalSaveIndex(uid: string): Promise<string[]> {
  const raw = await localforage.getItem<string[]>(localSaveIndexKey(uid))
  return Array.isArray(raw) ? raw : []
}

async function saveLocalSaveIndex(uid: string, saveIds: string[]): Promise<void> {
  const next = [...new Set(saveIds)]
  await localforage.setItem(localSaveIndexKey(uid), next)
}

async function writeLocalSnapshot(uid: string, saveId: string, state: GameState): Promise<void> {
  const textState = stripImages(state)
  const stateString = await gzip(JSON.stringify({ ...textState, history: [] }))
  const entry: LocalSaveEntry = {
    uid,
    saveId,
    lastStateTimestamp: state.meta.timestamp ?? Date.now(),
    compressed: true,
    stateString,
  }
  await localforage.setItem(localSaveKey(uid, saveId), entry)
  const idx = await loadLocalSaveIndex(uid)
  idx.push(saveId)
  await saveLocalSaveIndex(uid, idx)
}

async function readLocalSnapshot(uid: string, saveId: string): Promise<LocalSaveEntry | null> {
  const entry = await localforage.getItem<LocalSaveEntry>(localSaveKey(uid, saveId))
  if (!entry || entry.uid !== uid || entry.saveId !== saveId) return null
  return entry
}

async function findLocalSnapshotBySaveId(saveId: string): Promise<LocalSaveEntry | null> {
  const keys = await localforage.keys()
  const candidates = keys.filter(key => key.startsWith(LOCAL_SAVE_PREFIX) && key.endsWith(`_${saveId}`))
  if (!candidates.length) return null

  let newest: LocalSaveEntry | null = null
  for (const key of candidates) {
    const entry = await localforage.getItem<LocalSaveEntry>(key)
    if (!entry || entry.saveId !== saveId) continue
    if (!newest || (entry.lastStateTimestamp ?? 0) >= (newest.lastStateTimestamp ?? 0)) {
      newest = entry
    }
  }
  return newest
}

async function removeLocalSnapshot(uid: string, saveId: string): Promise<void> {
  await localforage.removeItem(localSaveKey(uid, saveId))
  const idx = await loadLocalSaveIndex(uid)
  await saveLocalSaveIndex(uid, idx.filter(id => id !== saveId))
}

export async function getPendingSaveCount(uid: string): Promise<number> {
  return (await syncManager.getPendingIds(uid)).length
}

export async function getSaveConflictBackupCount(uid: string): Promise<number> {
  const idx = await localforage.getItem<string[]>(`save_backups_index_${uid}`)
  return idx?.length ?? 0
}

export async function forceFlushPendingSaves(uid: string): Promise<void> {
  await syncManager.flush(uid)
}

export async function clearPendingSaves(uid: string): Promise<void> {
  const idx = await syncManager.getPendingIds(uid)
  const keys = idx.map(saveId => pendingSaveKey(uid, saveId))
  await Promise.allSettled(keys.map(k => localforage.removeItem(k)))
  await localforage.setItem(pendingIndexKey(uid), [])
}

export async function clearSaveConflictBackups(uid: string): Promise<void> {
  const idx = await localforage.getItem<string[]>(`save_backups_index_${uid}`) || []
  await Promise.allSettled(idx.map(k => localforage.removeItem(k)))
  await localforage.setItem(`save_backups_index_${uid}`, [])
}

export function startPendingSaveSync(uid: string): void {
  if (isLocalOnlyPersistence()) return
  syncManager.startAutoSync(uid)
}

export function stopPendingSaveSync(uid: string): void {
  if (isLocalOnlyPersistence()) return
  syncManager.stopAutoSync(uid)
}

// --- Save ---

export async function saveGame(uid: string, saveId: string, state: GameState): Promise<void> {
  await cacheLocalArtifacts(uid, saveId, state)
  await writeLocalSnapshot(uid, saveId, state)
  
  if (isLocalOnlyPersistence()) return

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    await syncManager.enqueue(uid, saveId, state)
    return
  }

  try {
    await cloudAdapter.saveGame(uid, saveId, state)
    await syncManager.flush(uid)
  } catch (e) {
    console.warn('[persist] save failed; queued for retry', e)
    await syncManager.enqueue(uid, saveId, state)
  }
}

// --- Load ---

function migrateState(state: GameState): GameState {
  state.companions = state.companions ?? []
  const world = state.world as GameState['world'] & { colorScheme?: unknown }
  world.colorScheme = WorldColorScheme.fromAI(world.colorScheme)
  const toItems = (inv: unknown[]) =>
    inv
      .map(i => typeof i === 'string' ? { name: i, equipped: false } : i)
      .filter(i => {
        const name = String((i as { name?: string })?.name ?? '').trim()
        return !!name
      })
  const toNames = (inv: unknown[]) =>
    inv
      .map(i => String(i ?? '').trim())
      .filter(Boolean)

  state.player.attributes = normalizeAttributeMetadata(state.player.attributes as Record<string, Partial<Attribute>>)
  state.player.inventory = Array.isArray(state.player?.inventory)
    ? toItems(state.player.inventory as unknown[]) as GameState['player']['inventory']
    : []
  state.player.skills = normalizeSkills(state.player.skills, state.player.attributes)
  state.companions.forEach(c => {
    c.attributes = normalizeAttributeMetadata(c.attributes as Record<string, Partial<Attribute>>)
    c.inventory = Array.isArray(c.inventory) ? toItems(c.inventory as unknown[]) as typeof c.inventory : []
    c.skills = normalizeSkills(c.skills, c.attributes)
    c.abilities &&      c.abilities.forEach(a => { a.args = a.args ?? []; })
  })
  state.player.abilities &&  state.player.abilities.forEach(a => { a.args = a.args ?? []; })
  state.activeEvent = parseGameEventState(state.activeEvent) ?? null
  // Living World System fields (added 2026-03-03)
  state.worldLog = state.worldLog ?? []
  state.world.clocks = normalizeProgressClocks(state.world.clocks)
  state.world.scheduledJobs = normalizeScheduledEffectJobs(state.world.scheduledJobs)
  state.world.npcs = state.world.npcs ?? []
  state.world.narrative = state.world.narrative ?? ''
  state.world.lastTick = state.world.lastTick ?? 0
  Object.values(state.atlas.nodes).forEach(n => {
    n.items = n.items ?? []
    n.dormantFlags = n.dormantFlags ?? []
    n.clocks = normalizeProgressClocks(n.clocks)
  })
  state.world.npcs.forEach(n => {
    n.relationship = n.relationship ?? 0
    n.memory = n.memory ?? []
    n.inventory = Array.isArray(n.inventory) ? toNames(n.inventory as unknown[]) : []
    n.activeEffects = n.activeEffects ?? []
    n.dormantEffects = n.dormantEffects ?? []
  })
  if (state.currentRoom?.npcs) {
    state.currentRoom.npcs.forEach(n => {
      n.relationship = n.relationship ?? 0
      n.memory = n.memory ?? []
      n.inventory = Array.isArray(n.inventory) ? toNames(n.inventory as unknown[]) : []
      n.activeEffects = n.activeEffects ?? []
      n.dormantEffects = n.dormantEffects ?? []
    })
  }
  state.currentRoom.dormantFlags = state.currentRoom.dormantFlags ?? []
  state.currentRoom.clocks = normalizeProgressClocks(state.currentRoom.clocks)
  state.player.dormantEffects = state.player.dormantEffects ?? []
  state.companions.forEach(c => { c.dormantEffects = c.dormantEffects ?? []; })
  // Migrate currentRoom.npcs → world.npcs on old saves
  if (state.world.npcs.length === 0 && state.currentRoom?.npcs?.length > 0) {
    const loc = locationKey(state.player.location)
    state.world.npcs = state.currentRoom.npcs
      .filter(n => n?.name)
      .map(n => ({
        id: generateNpcId(n.name),
        name: n.name,
        identity: n.identity ?? '',
        lore: n.lore ?? '',
        sex: n.sex,
        intentions: n.intentions ?? [],
        feelings: n.feelings ?? {},
        location: loc,
        lastActed: 0,
        relationship: n.relationship ?? 0,
        memory: n.memory ?? [],
        activeEffects: n.activeEffects ?? [],
        dormantEffects: n.dormantEffects ?? [],
      }))
  }

  return state
}

async function parseStateFromDoc(data: Record<string, unknown>): Promise<GameState> {
  if (!data.stateString) throw new Error('No state data found')
  const jsonStr = data.compressed
    ? await gunzip(data.stateString as string)
    : data.stateString as string
  return migrateState(JSON.parse(jsonStr))
}

async function hydrateLocalArtifacts(saveId: string, state: GameState): Promise<void> {
  // Load global images and history
  const [localPortrait, localHistory] = await Promise.all([
    localforage.getItem<string>(`portrait_${saveId}`),
    localforage.getItem<GameState['history']>(`history_${saveId}`),
  ])
  if (localPortrait) state.portraitUrl = localPortrait
  if (localHistory) state.history = localHistory
  // Load node-specific and companion images in parallel to avoid N serial IndexedDB reads.
  const playerLocKey = locationKey(state.player.location)
  const nodeKeys = Object.keys(state.atlas.nodes)
  const visionEntries = await Promise.all(
    nodeKeys.map(async (key) => [key, await localforage.getItem<string>(`vision_${saveId}_${key}`)] as const),
  )
  for (const [key, vision] of visionEntries) {
    if (vision) state.atlas.nodes[key].visionUrl = vision
    if (key === playerLocKey) state.visionUrl = vision ?? undefined
  }

  if (state.companions) {
    const portraitEntries = await Promise.all(
      state.companions.map(async (c) => [c, await localforage.getItem<string>(`companion_portrait_${saveId}_${c.name}`)] as const),
    )
    for (const [companion, portrait] of portraitEntries) {
      if (portrait) companion.portraitUrl = portrait
    }
  }
}

export async function loadGame(uid: string, saveId: string): Promise<GameState | null> {
  if (isLocalOnlyPersistence()) {
    const localEntry = await readLocalSnapshot(uid, saveId) || await findLocalSnapshotBySaveId(saveId)
    if (!localEntry) return null
    const state = await parseStateFromDoc(localEntry as unknown as Record<string, unknown>)
    await hydrateLocalArtifacts(saveId, state)
    return state
  }

  let localState: GameState | null = null
  try {
    const localEntry = await readLocalSnapshot(uid, saveId) || await findLocalSnapshotBySaveId(saveId)
    if (localEntry) {
      localState = await parseStateFromDoc(localEntry as unknown as Record<string, unknown>)
    }
  } catch (e) {
    console.warn('[persist] load local snapshot read failed; continuing with cloud/pending state', e)
  }

  const pendingIds = await syncManager.getPendingIds(uid)
  const hasPending = pendingIds.includes(saveId)
  let cloudState: GameState | null = null
  try {
    cloudState = await withFirestoreTimeout(
      `load cloud state ${saveId}`,
      cloudAdapter.loadGame(uid, saveId)
    )
    if (cloudState) cloudState = migrateState(cloudState)
  } catch (e) {
    console.warn('[persist] load cloud read failed; falling back to local pending save', e)
  }

  let state: GameState | null = cloudState
  if (!state && localState) {
    state = localState
  }
  if (!state && hasPending) {
    const pending = await localforage.getItem<any>(pendingSaveKey(uid, saveId))
    if (pending) {
      const json = pending.compressed ? await gunzip(pending.stateString) : pending.stateString
      state = migrateState(JSON.parse(json))
    }
  }
  if (!state) return null

  // Merge/Sync logic...
  if (hasPending) {
    const pending = await localforage.getItem<any>(pendingSaveKey(uid, saveId))
    if (pending) {
      const json = pending.compressed ? await gunzip(pending.stateString) : pending.stateString
      const pendingState = migrateState(JSON.parse(json))
      const pendingTs = pending.metaTimestamp || pending.queuedAt
      const currentTs = state.meta.timestamp ?? 0
      if (pendingTs > currentTs) {
        state = pendingState
      }
    }
  }
  if (localState) {
    const localTs = localState.meta.timestamp ?? 0
    const currentTs = state.meta.timestamp ?? 0
    if (localTs > currentTs) state = localState
  }

  await hydrateLocalArtifacts(saveId, state)
  return state
}

export async function exportSaveBackup(uid: string, saveId: string): Promise<string> {
  const snapshot = await loadGame(uid, saveId)
  if (!snapshot) throw new Error('Save not found')
  return JSON.stringify({
    version: 1,
    exportedAt: Date.now(),
    saveId,
    state: snapshot,
  }, null, 2)
}

export async function importSaveBackup(uid: string, raw: string, preferredSaveId?: string): Promise<string> {
  const parsed = JSON.parse(raw) as { saveId?: string; state?: GameState } | GameState
  const source = (parsed as { state?: GameState }).state ?? (parsed as GameState)
  if (!source?.player || !source?.world || !source?.atlas) {
    throw new Error('Invalid backup payload')
  }
  const state = migrateState(source)
  const saveId = preferredSaveId || (parsed as { saveId?: string }).saveId || Date.now().toString()
  state.meta = {
    ...state.meta,
    timestamp: Date.now(),
    name: state.meta?.name || state.player?.identity || 'Imported Save',
  }
  await saveGame(uid, saveId, state)
  return saveId
}

// --- Delete save ---

export async function deleteGame(uid: string, saveId: string): Promise<void> {
  if (!isLocalOnlyPersistence()) {
    try {
      await withFirestoreTimeout(`delete save ${saveId}`, cloudAdapter.deleteGame(uid, saveId))
    } catch (e) {
      console.warn('[persist] cloud delete failed; cleaning local artifacts only', e)
    }
  }
  
  const keysToRemove = [
    `vision_${saveId}`,
    `portrait_${saveId}`,
    `history_${saveId}`,
    pendingSaveKey(uid, saveId),
  ]
  const keys = await localforage.keys()
  for (const k of keys) {
    if (k.startsWith(`companion_portrait_${saveId}_`)) {
      keysToRemove.push(k)
    }
    if (k.startsWith(`${SAVE_BACKUP_PREFIX}${uid}_${saveId}_`)) {
      keysToRemove.push(k)
    }
  }
  const idx = await syncManager.getPendingIds(uid)
  await localforage.setItem(pendingIndexKey(uid), idx.filter(id => id !== saveId))
  await removeLocalSnapshot(uid, saveId)
  await Promise.allSettled(keysToRemove.map(k => localforage.removeItem(k)))
}

// --- List saves (first 20) ---

export async function listSaves(uid: string): Promise<SaveMeta[]> {
  const saves: SaveMeta[] = []
  const upsertSave = (id: string, state: SaveMeta['state']) => {
    const idx = saves.findIndex(s => s.id === id)
    if (idx < 0) {
      saves.push({ id, state })
      return
    }
    const prevTs = saves[idx].state.meta.timestamp ?? 0
    const nextTs = state.meta.timestamp ?? 0
    if (nextTs >= prevTs) saves[idx] = { id, state }
  }
  if (isLocalOnlyPersistence()) {
    const saveIds = await loadLocalSaveIndex(uid)
    for (const saveId of [...new Set(saveIds)]) {
      const local = await readLocalSnapshot(uid, saveId)
      if (!local) continue
      try {
        const state = await parseStateFromDoc(local as unknown as Record<string, unknown>)
        upsertSave(saveId, state as SaveMeta['state'])
      } catch {
        // malformed local save — skip
      }
    }
    return saves.sort((a, b) => (b.state.meta.timestamp ?? 0) - (a.state.meta.timestamp ?? 0))
  }

  try {
    const cloudSaves = await withFirestoreTimeout(`list cloud saves for ${uid}`, cloudAdapter.listSaves(uid))
    cloudSaves.forEach(s => upsertSave(s.id, s.state))
  } catch (e) {
    console.warn('[persist] list cloud read failed; showing local pending saves only', e)
  }
  const localIds = await loadLocalSaveIndex(uid)
  for (const saveId of [...new Set(localIds)]) {
    const local = await readLocalSnapshot(uid, saveId)
    if (!local) continue
    try {
      const state = await parseStateFromDoc(local as unknown as Record<string, unknown>)
      upsertSave(saveId, state as SaveMeta['state'])
    } catch {
      // malformed local save — skip
    }
  }
  const pendingIds = await syncManager.getPendingIds(uid)
  for (const saveId of [...new Set(pendingIds)]) {
    const pending = await localforage.getItem<any>(pendingSaveKey(uid, saveId))
    if (!pending) continue
    const json = pending.compressed ? await gunzip(pending.stateString) : pending.stateString
    const pendingState = migrateState(JSON.parse(json))
    const pendingTs = pending.metaTimestamp || pending.queuedAt
    const current = saves.find(s => s.id === saveId)
    const currentTs = current?.state?.meta?.timestamp ?? 0
    if (pendingTs >= currentTs) upsertSave(saveId, pendingState as SaveMeta['state'])
  }
  return saves.sort((a, b) => (b.state.meta.timestamp ?? 0) - (a.state.meta.timestamp ?? 0))
}

// --- Multiverse: Export Blueprint (world seed + atlas) ---

export async function exportBlueprint(code: string, state: GameState): Promise<void> {
  const blueprint = {
    seed: state.world.seed,
    atlas: state.atlas,
    room: state.currentRoom,
  }
  await cloudAdapter.exportArtifact(code, JSON.stringify(blueprint), 'blueprint')
}

function buildSharedPlayerProfile(state: GameState): SharedPlayerProfile {
  return {
    name: state.player.name,
    identity: state.player.identity,
    bio: state.player.bio,
    sex: state.player.sex,
    vitals: structuredClone(state.player.vitals),
    attributes: structuredClone(state.player.attributes),
    skills: structuredClone(state.player.skills),
    abilities: structuredClone(state.player.abilities),
    traits: structuredClone(state.player.traits),
    inventory: structuredClone(state.player.inventory),
  }
}

// --- Multiverse: Export Scenario (full state snapshot) ---

export async function exportScenario(code: string, state: GameState): Promise<void> {
  const textState = stripImages(state)
  const payload = JSON.stringify(textState)
  saveLocalPublicArtifact(LOCAL_PUBLIC_SCENARIOS_KEY, code, payload)
  try {
    await cloudAdapter.exportArtifact(code, payload, 'scenario')
  } catch {
    // Local starter export remains usable even if the remote write is denied.
  }
}

export async function exportVesselProfile(code: string, state: GameState): Promise<void> {
  const payload = JSON.stringify(buildSharedPlayerProfile(state))
  saveLocalPublicArtifact(LOCAL_PUBLIC_VESSELS_KEY, code, payload)
  try {
    await cloudAdapter.exportArtifact(code, payload, 'vessel')
  } catch {
    // Local starter export remains usable even if the remote write is denied.
  }
}

export async function exportStarterKit(code: string, state: GameState): Promise<void> {
  const starter: SharedStarterKit = {
    seed: state.world.seed,
    atlas: state.atlas,
    room: state.currentRoom,
    player: buildSharedPlayerProfile(state),
  }
  const payload = JSON.stringify(starter)
  saveLocalPublicArtifact(LOCAL_PUBLIC_STARTERS_KEY, code, payload)
  try {
    await cloudAdapter.exportArtifact(code, payload, 'starter')
  } catch {
    // Local starter export remains usable even if the remote write is denied.
  }
}

// --- Multiverse: Import (blueprint or scenario) ---

export async function importByCode(code: string): Promise<ImportResult> {
  const localBlueprint = readLocalPublicArtifact(LOCAL_PUBLIC_BLUEPRINTS_KEY, code)
  if (localBlueprint) return { type: 'blueprint', data: JSON.parse(localBlueprint) }

  const localScenario = readLocalPublicArtifact(LOCAL_PUBLIC_SCENARIOS_KEY, code)
  if (localScenario) return { type: 'scenario', state: JSON.parse(localScenario) }

  const localVessel = readLocalPublicArtifact(LOCAL_PUBLIC_VESSELS_KEY, code)
  if (localVessel) return { type: 'vessel', data: JSON.parse(localVessel) }

  const localStarter = readLocalPublicArtifact(LOCAL_PUBLIC_STARTERS_KEY, code)
  if (localStarter) return { type: 'starter', data: JSON.parse(localStarter) }

  try {
    const cloudResult = await cloudAdapter.importByCode(code)
    if (cloudResult.type !== 'not_found') {
      return cloudResult
    }
  } catch {
    // local fallback already checked
  }

  return { type: 'not_found' }
}

// --- Utility ---

export function generateShareCode(): string {
  return Math.random().toString(36).substring(2, 8).toUpperCase()
}

// --- Migration ---

export async function migrateGuestSaves(oldUid: string, newUid: string): Promise<void> {
  if (!oldUid || !newUid || oldUid === newUid) return

  const localSaveIds = await loadLocalSaveIndex(oldUid)
  for (const saveId of localSaveIds) {
    const entry = await readLocalSnapshot(oldUid, saveId)
    if (entry) {
      entry.uid = newUid
      await localforage.setItem(localSaveKey(newUid, saveId), entry)
      await syncManager.enqueue(newUid, saveId, await parseStateFromDoc(entry as any))
    }
    await removeLocalSnapshot(oldUid, saveId)
  }

  await saveLocalSaveIndex(newUid, [...new Set([...await loadLocalSaveIndex(newUid), ...localSaveIds])])

  const pendingSaveIds = await syncManager.getPendingIds(oldUid)
  for (const saveId of pendingSaveIds) {
    const entry = await localforage.getItem<any>(pendingSaveKey(oldUid, saveId))
    if (entry) {
      entry.uid = newUid
      await localforage.setItem(pendingSaveKey(newUid, saveId), entry)
    }
    await localforage.removeItem(pendingSaveKey(oldUid, saveId))
  }

  if (!isLocalOnlyPersistence()) {
    syncManager.flush(newUid).catch(console.warn)
  }
}
